import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import RsvpCard, { type RsvpRecord } from './components/RsvpCard';

type TabType = 'upcoming' | 'past';

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
      <div className="h-1 w-full bg-gray-200" />
      <div className="p-5 space-y-3">
        <div className="flex gap-2">
          <div className="h-4 w-16 bg-gray-200 rounded-full" />
          <div className="h-4 w-12 bg-gray-200 rounded-full" />
        </div>
        <div className="h-5 w-3/4 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 gap-2.5 mt-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-6 h-6 bg-gray-200 rounded-lg shrink-0" />
              <div className="space-y-1 flex-1">
                <div className="h-2.5 w-8 bg-gray-200 rounded" />
                <div className="h-3 w-20 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
        <div className="h-9 w-full bg-gray-200 rounded-xl mt-2" />
      </div>
    </div>
  );
}

function EmptyState({ tab }: { tab: TabType }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20 text-center px-4">
      <div className="w-20 h-20 flex items-center justify-center bg-pink-50 rounded-full mb-5">
        <i className={`${tab === 'upcoming' ? 'ri-calendar-line' : 'ri-history-line'} text-pink-400 text-3xl`} />
      </div>
      <h3 className="text-lg font-bold text-gray-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
        {tab === 'upcoming' ? 'No upcoming RSVPs yet' : 'No past events'}
      </h3>
      <p className="text-sm text-gray-500 mb-6 max-w-xs leading-relaxed">
        {tab === 'upcoming'
          ? "You haven't reserved any upcoming spots. Browse events and grab your seat!"
          : "Events you've attended will show up here."}
      </p>
      {tab === 'upcoming' && (
        <Link
          to="/events"
          className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-full transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-calendar-event-line text-base" />
          Browse Events
        </Link>
      )}
    </div>
  );
}

export default function MyRsvpsPage() {
  const { user, isLoggedIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [rsvps, setRsvps] = useState<RsvpRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabType>('upcoming');
  const [membershipTier, setMembershipTier] = useState<string>('insider');

  useEffect(() => {
    if (!authLoading && !isLoggedIn) {
      navigate('/events');
    }
  }, [authLoading, isLoggedIn, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setLoading(true);

      const [rsvpRes, profileRes] = await Promise.all([
        supabase
          .from('rsvps')
          .select('id, event_title, event_date, event_time, event_location, ticket_type, quantity, total_amount, payment_status, created_at')
          .eq('user_id', user.id)
          .order('event_date', { ascending: true }),
        supabase
          .from('profiles')
          .select('membership_tier')
          .eq('id', user.id)
          .maybeSingle(),
      ]);

      if (!rsvpRes.error && rsvpRes.data) {
        setRsvps(rsvpRes.data as RsvpRecord[]);
      }
      if (!profileRes.error && profileRes.data?.membership_tier) {
        setMembershipTier(profileRes.data.membership_tier);
      }

      setLoading(false);
    };
    fetchData();
  }, [user]);

  const isVipMember = membershipTier === 'vip';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcoming = rsvps.filter((r) => new Date(r.event_date + 'T00:00:00') >= today);
  const past = rsvps.filter((r) => new Date(r.event_date + 'T00:00:00') < today).reverse();
  const displayed = tab === 'upcoming' ? upcoming : past;

  const nextEvent = upcoming[0] ?? null;

  return (
    <div className="min-h-screen bg-gray-50/50">
      <SEO
        title="My RSVPs | The Mommy Mingle"
        description="View and manage your upcoming RSVP reservations for Mommy Mingle events in Gwinnett County."
        keywords="my RSVPs, event reservations, Mommy Mingle events"
        canonical="/my-rsvps"
      />
      <Navbar />

      <div className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Page header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 flex items-center justify-center bg-pink-100 rounded-full">
                <i className="ri-heart-fill text-pink-500 text-sm" />
              </div>
              <span className="text-[11px] font-bold text-pink-500 uppercase tracking-widest">
                The Mommy Mingle
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                {user ? `Hi, ${(user as { firstName?: string }).firstName ?? 'there'}!` : 'My RSVPs'}
              </h1>
              {/* TMM VIP membership status badge — header level */}
              {!loading && isVipMember && (
                <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-rose-400 text-white text-xs font-extrabold px-3 py-1.5 rounded-full">
                  <i className="ri-vip-crown-2-fill text-sm" />
                  TMM VIP Member
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {isVipMember
                ? 'Your VIP perks are active — early access, reserved spots, no fees.'
                : 'All your reserved spots in one place.'}
            </p>
          </div>

          {/* VIP upgrade nudge for non-VIP members with RSVPs */}
          {!loading && !isVipMember && rsvps.length > 0 && (
            <Link
              to="/membership"
              className="flex items-center gap-3 bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200 rounded-2xl px-5 py-4 mb-8 hover:border-amber-300 transition-colors cursor-pointer group"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl shrink-0">
                <i className="ri-vip-crown-2-fill text-amber-500 text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900">Unlock TMM VIP — $12/month</p>
                <p className="text-xs text-gray-500">Get early RSVP access, VIP-only events, and no fees on most events.</p>
              </div>
              <i className="ri-arrow-right-line text-amber-400 group-hover:translate-x-1 transition-transform shrink-0" />
            </Link>
          )}

          {/* Stats bar */}
          {!loading && rsvps.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { label: 'Total RSVPs', value: rsvps.length, icon: 'ri-ticket-2-line', color: 'text-pink-500', bg: 'bg-pink-50' },
                { label: 'Upcoming', value: upcoming.length, icon: 'ri-calendar-event-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
                { label: 'Attended', value: past.length, icon: 'ri-checkbox-circle-line', color: 'text-gray-500', bg: 'bg-gray-100' },
              ].map(({ label, value, icon, color, bg }) => (
                <div key={label} className="bg-white rounded-2xl border border-gray-100 px-4 py-4 text-center">
                  <div className={`w-9 h-9 flex items-center justify-center ${bg} rounded-xl mx-auto mb-2`}>
                    <i className={`${icon} ${color} text-lg`} />
                  </div>
                  <div className="text-2xl font-black text-gray-900">{value}</div>
                  <div className="text-[11px] text-gray-400 font-medium mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Next event spotlight */}
          {!loading && nextEvent && (
            <div className={`bg-gradient-to-r ${isVipMember ? 'from-amber-500 to-rose-500' : 'from-pink-500 to-rose-500'} rounded-2xl p-5 mb-8 text-white`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-2">
                    <i className="ri-time-fill text-white/80 text-xs" />
                    <span className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                      {(() => {
                        const d = Math.round((new Date(nextEvent.event_date + 'T00:00:00').getTime() - today.getTime()) / 86400000);
                        if (d === 0) return "Today!";
                        if (d === 1) return "Tomorrow";
                        return `In ${d} days`;
                      })()}
                    </span>
                    {isVipMember && (
                      <span className="ml-2 inline-flex items-center gap-1 bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        <i className="ri-vip-crown-fill text-xs" />
                        VIP Reserved
                      </span>
                    )}
                  </div>
                  <h2 className="text-lg font-bold leading-snug mb-1 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {nextEvent.event_title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-3 text-white/80">
                    <span className="flex items-center gap-1 text-xs">
                      <i className="ri-calendar-line text-sm" />
                      {new Date(nextEvent.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1 text-xs">
                      <i className="ri-time-line text-sm" />
                      {nextEvent.event_time}
                    </span>
                    <span className="flex items-center gap-1 text-xs">
                      <i className="ri-map-pin-line text-sm" />
                      {nextEvent.event_location}
                    </span>
                  </div>
                </div>
                <a
                  href={`https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(nextEvent.event_title)}&dates=${nextEvent.event_date.replace(/-/g, '')}/${nextEvent.event_date.replace(/-/g, '')}&location=${encodeURIComponent(nextEvent.event_location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 bg-white/20 hover:bg-white/30 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
                >
                  <i className="ri-calendar-check-line text-sm" />
                  Add Calendar
                </a>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full w-fit mb-6">
            {(['upcoming', 'past'] as TabType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  tab === t ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'upcoming' ? (
                  <><i className="ri-calendar-event-line text-sm" /> Upcoming {!loading && <span className="ml-0.5 text-pink-500">({upcoming.length})</span>}</>
                ) : (
                  <><i className="ri-history-line text-sm" /> Past {!loading && <span className="ml-0.5 text-gray-400">({past.length})</span>}</>
                )}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {loading ? (
              [1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)
            ) : displayed.length === 0 ? (
              <EmptyState tab={tab} />
            ) : (
              displayed.map((rsvp) => (
                <RsvpCard key={rsvp.id} rsvp={rsvp} isPast={tab === 'past'} isVipMember={isVipMember} />
              ))
            )}
          </div>

          {/* Browse more CTA */}
          {!loading && rsvps.length > 0 && (
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/events"
                className="inline-flex items-center gap-2 px-7 py-3 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-event-line text-base" />
                Browse More Events
              </Link>
              {!isVipMember && (
                <Link
                  to="/membership"
                  className="inline-flex items-center gap-2 px-7 py-3 border border-amber-200 hover:border-amber-300 text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 text-sm font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-vip-crown-line text-base" />
                  Upgrade to VIP
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
